import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import OnboardingPipelineCard from './components/OnboardingPipelineCard';
import TaskManagementPanel from './components/TaskManagementPanel';
import DocumentCollectionPortal from './components/DocumentCollectionPortal';
import ProgressTrackingDashboard from './components/ProgressTrackingDashboard';
import BiometricSecurityDisclaimer from '../../components/navigation/BiometricSecurityDisclaimer';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const EmployeeOnboardingWorkflowCenter = () => {
  const [activeTab, setActiveTab] = useState('pre-boarding');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDocumentPortal, setShowDocumentPortal] = useState(false);
  const [globalFilters, setGlobalFilters] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const handleFilterChange = (filters) => {
    setGlobalFilters(filters);
  };

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (hasActiveSubscription) {
      loadOnboardingData();
    }
  }, [activeTab, hasActiveSubscription]);

  const checkSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError('');

      // Get company ID from company_settings (assuming single company setup)
      const { data: companyData, error: companyError } = await supabase
        ?.from('company_settings')
        ?.select('id')
        ?.limit(1)
        ?.maybeSingle();

      if (companyError) throw companyError;

      if (!companyData) {
        setError('Company settings not found. Please contact support.');
        setLoading(false);
        return;
      }

      // Check subscription status
      const subscription = await billingService?.getSubscriptionDetails(companyData?.id);
      setSubscriptionStatus(subscription);

      // Only allow access if subscription is active
      if (subscription?.subscriptionStatus === 'active') {
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(false);
        setCandidates([]);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error checking subscription:', err);
      setError(err?.message || 'Failed to verify subscription status');
      setHasActiveSubscription(false);
      setLoading(false);
    }
  };

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load actual employee data from database
      // Since employees table is empty, this will return empty array
      // In production, this would fetch real onboarding candidates
      setCandidates([]);

      setLoading(false);
    } catch (err) {
      setError(err?.message || 'Failed to load onboarding data');
    }
  };

  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleDocumentCollection = (candidate) => {
    setSelectedCandidate(candidate);
    setShowDocumentPortal(true);
  };

  const tabs = [
    { id: 'pre-boarding', label: 'Pre-Boarding', icon: 'FileText', count: 1 },
    { id: 'first-day', label: 'First Day', icon: 'Calendar', count: 1 },
    { id: 'first-week', label: 'First Week', icon: 'Clock', count: 1 },
    { id: '90-day', label: '90-Day Milestone', icon: 'Award', count: 1 }
  ];

  const pipelineStats = {
    activeCandidates: 4,
    averageCompletion: 76,
    overdueTasksCount: 2,
    documentsCollected: 21,
    documentsRequired: 24
  };

  const renderPaymentRequiredMessage = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <div className="text-center max-w-md">
        <div className="mb-4">
          <Icon name="lock" className="w-16 h-16 text-red-500 mx-auto" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Payment Required
        </h3>
        <p className="text-gray-600 mb-6">
          Access to employee information is restricted until payment is received. 
          {subscriptionStatus?.subscriptionStatus === 'suspended' && ' Your subscription has been suspended due to non-payment.'}
          {subscriptionStatus?.subscriptionStatus === 'cancelled'&& ' Your subscription has been cancelled.'}
          {!subscriptionStatus && 
            ' No active subscription found for your account.'}
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>To activate your subscription:</strong>
          </p>
          <ul className="text-sm text-blue-700 mt-2 space-y-1 text-left">
            <li>• Complete payment through your billing portal</li>
            <li>• Contact finance team for payment confirmation</li>
            <li>• Allow 24-48 hours for payment processing</li>
          </ul>
        </div>
        <Button
          onClick={() => window.location.href = '/billing-subscription-management'}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Go to Billing
        </Button>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <div className="text-center max-w-md">
        <div className="mb-4">
          <Icon name="users" className="w-16 h-16 text-gray-400 mx-auto" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          No Onboarding Candidates
        </h3>
        <p className="text-gray-600 mb-6">
          There are currently no employees in the onboarding pipeline for this stage.
        </p>
        <p className="text-sm text-gray-500">
          New candidates will appear here once they are added to the system.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Employee Onboarding Workflow Center</h1>
          <p className="mt-2 text-gray-600">Streamline new hire onboarding with automated workflows and task management</p>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Icon name="alert-circle" className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!loading && !hasActiveSubscription && renderPaymentRequiredMessage()}

        {!loading && hasActiveSubscription && (
          <>
            {/* Stage Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {[
                    { id: 'pre-boarding', label: 'Pre-Boarding', icon: 'file-text' },
                    { id: 'first-day', label: 'First Day', icon: 'calendar' },
                    { id: 'first-week', label: 'First Week', icon: 'clock' },
                    { id: '90-day', label: '90-Day Review', icon: 'check-circle' }
                  ]?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab?.id
                          ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon name={tab?.icon} className="w-4 h-4 mr-2" />
                      {tab?.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Pipeline Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {candidates?.length === 0 ? (
                <div className="col-span-full">
                  {renderEmptyState()}
                </div>
              ) : (
                candidates?.map((candidate) => (
                  <OnboardingPipelineCard
                    key={candidate?.id}
                    candidate={candidate}
                    stats={pipelineStats}
                    onSelect={setSelectedCandidate}
                  />
                ))
              )}
            </div>

            {/* Task Management and Progress Tracking */}
            {selectedCandidate && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <TaskManagementPanel 
                  candidate={selectedCandidate}
                  onClose={() => setSelectedCandidate(null)}
                />
                <ProgressTrackingDashboard 
                  candidate={selectedCandidate}
                  candidates={candidates}
                />
              </div>
            )}

            {/* Document Collection Portal */}
            {showDocumentPortal && selectedCandidate && (
              <DocumentCollectionPortal
                candidate={selectedCandidate}
                onClose={() => setShowDocumentPortal(false)}
              />
            )}
          </>
        )}
      </div>

      <BiometricSecurityDisclaimer />
    </div>
  );
};

export default EmployeeOnboardingWorkflowCenter;